"use client";

import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from "react";

export interface UserTrackingData {
  ip?: string;
  trackId?: string;
  campaignId?: string;
  gclid?: string;
  fullUrl?: string;
  pagePath?: string;
}

export interface AppState {
  userTracking: UserTrackingData;
  analytics: {
    pageViews: number;
    sessionId: string;
    userId?: string;
  };
}

type AppAction =
  | { type: "SET_USER_TRACKING"; payload: Partial<UserTrackingData> }
  | { type: "SET_ANALYTICS"; payload: Partial<AppState["analytics"]> }
  | { type: "INCREMENT_PAGE_VIEWS" }
  | { type: "HYDRATE_FROM_STORAGE"; payload: Partial<AppState> };

const initialState: AppState = {
  userTracking: {},
  analytics: {
    pageViews: 0,
    sessionId: "",
  },
};

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case "SET_USER_TRACKING":
      return {
        ...state,
        userTracking: { ...state.userTracking, ...action.payload },
      };

    case "SET_ANALYTICS":
      return {
        ...state,
        analytics: { ...state.analytics, ...action.payload },
      };

    case "INCREMENT_PAGE_VIEWS":
      return {
        ...state,
        analytics: {
          ...state.analytics,
          pageViews: (state.analytics.pageViews || 0) + 1,
        },
      };

    case "HYDRATE_FROM_STORAGE":
      return { ...state, ...action.payload };

    default:
      return state;
  }
};

const AppStateContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

interface AppStateProviderProps {
  children: ReactNode;
}

export const AppStateProvider: React.FC<AppStateProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const existingData: Partial<AppState> = {};

        const ip = localStorage.getItem("ip");
        const trackId = localStorage.getItem("track");
        const seoParams = localStorage.getItem("seo_params");

        if (ip || trackId || seoParams) {
          existingData.userTracking = {
            ip: ip || undefined,
            trackId: trackId || undefined,
            ...(seoParams ? JSON.parse(seoParams) : {}),
          };
        }

        let sessionId = sessionStorage.getItem("sessionId");
        if (!sessionId) {
          sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem("sessionId", sessionId);
        }

        existingData.analytics = {
          sessionId,
          pageViews: 0,
        };

        if (Object.keys(existingData).length > 0) {
          dispatch({ type: "HYDRATE_FROM_STORAGE", payload: existingData });
        }
      } catch (error) {
        console.error("Error hydrating from localStorage:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        if (state.userTracking.ip) {
          localStorage.setItem("ip", state.userTracking.ip);
        }
        if (state.userTracking.trackId) {
          localStorage.setItem("track", state.userTracking.trackId);
        }
        if (state.userTracking.campaignId || state.userTracking.gclid) {
          localStorage.setItem(
            "seo_params",
            JSON.stringify({
              tk: state.userTracking.campaignId,
              gclid: state.userTracking.gclid,
            }),
          );
        }
      } catch (error) {
        console.error("Error persisting to localStorage:", error);
      }
    }
  }, [
    state.userTracking.ip,
    state.userTracking.trackId,
    state.userTracking.campaignId,
    state.userTracking.gclid,
  ]);

  return (
    <AppStateContext.Provider value={{ state, dispatch }}>
      {children}
    </AppStateContext.Provider>
  );
};

export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error("useAppState must be used within an AppStateProvider");
  }
  return context;
};

export const useUserTracking = () => {
  const { state, dispatch } = useAppState();

  const setUserTracking = useCallback(
    (data: Partial<UserTrackingData>) => {
      dispatch({ type: "SET_USER_TRACKING", payload: data });
    },
    [dispatch],
  );

  return { userTracking: state.userTracking, setUserTracking };
};

export const useAnalytics = () => {
  const { state, dispatch } = useAppState();

  const setAnalytics = useCallback(
    (analyticsData: Partial<AppState["analytics"]>) => {
      dispatch({ type: "SET_ANALYTICS", payload: analyticsData });
    },
    [dispatch],
  );

  const incrementPageViews = useCallback(() => {
    dispatch({ type: "INCREMENT_PAGE_VIEWS" });
  }, [dispatch]);

  return { analytics: state.analytics, setAnalytics, incrementPageViews };
};
